import { ratioColor, ratioRadius } from '../../lib/map-utils';

/**
 * Floating legend overlay anchored at the bottom-left of the map.
 * Two scales side-by-side (horizontal layout) so the legend occupies
 * less vertical real estate above the map controls:
 *
 *   1. "Credentials-to-jobs ratio" — a left-to-right red→green
 *      gradient with tick labels at 0 / 0.5 / 1.0 / 1.5. Below 1.0 is
 *      red/orange (shortage); above 1.0 is green (surplus). No
 *      "balanced" band — the report doesn't define one.
 *   2. "Bubble size" — three sample bubbles sized by the same
 *      `ratioRadius()` function the map uses, with low and high
 *      anchor labels. Smaller ratio = larger bubble so severe
 *      shortages stand out.
 *
 * Two variants:
 *   - `floating` (default) — absolute-positioned over the desktop map,
 *     hidden on mobile via `md:flex` because the absolute card would
 *     dominate the narrow viewport.
 *   - `inline` — renders in normal document flow (no absolute
 *     positioning, no `md:flex` gate). Used by `MapMobileLayout` to
 *     park the legend between the map and the scroll-hint banner so
 *     mobile users still see the color + size key.
 */
const RATIO_TICKS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0.0, label: '0' },
  { value: 0.5, label: '0.5' },
  { value: 1.0, label: '1.0' },
  { value: 1.5, label: '1.5' },
];

// Build the CSS gradient inline so it stays in sync with `ratioColor()`
// without us having to maintain a parallel stop list.
function buildRatioGradient(): string {
  const stops: string[] = [];
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    // Domain 0–1.8 to match `ratioColor`'s green endpoint.
    const ratio = t * 1.8;
    stops.push(`${ratioColor(ratio)} ${t * 100}%`);
  }
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

// Three sample ratios for the bubble-size scale: a steep shortage, a
// near-balance, and a strong surplus. The rendered pixel size mirrors
// the proportions `ratioRadius()` produces on the map.
const SIZE_SAMPLES: ReadonlyArray<{ ratio: number; label: string }> = [
  { ratio: 0.3, label: '0.3' },
  { ratio: 1.0, label: '1.0' },
  { ratio: 1.6, label: '1.6' },
];

function sampleSizePx(ratio: number): number {
  // Map the `ratioRadius()` meters output to a tiny on-screen
  // px range (8–22 px) so the three samples fit inside the
  // legend's compact width.
  const meters = ratioRadius(ratio);
  const min = ratioRadius(1.5);
  const max = ratioRadius(0.0);
  const t = (meters - min) / (max - min);
  return Math.round(8 + t * 14);
}

interface MapLegendProps {
  /** `floating` = absolute desktop overlay (default). `inline` = in flow for mobile. */
  readonly variant?: 'floating' | 'inline';
}

export function MapLegend({ variant = 'floating' }: MapLegendProps = {}) {
  const gradient = buildRatioGradient();
  const isInline = variant === 'inline';
  return (
    <section
      aria-label="Map legend"
      className={
        isInline
          ? // Inline (mobile): no-wrap row, ratio takes the flexible
            // remainder, bubble-size hugs to the right. No floating
            // card shadow — sits flush between the map and the
            // scroll-down banner.
            'flex items-start gap-3 border-b border-border bg-white px-4 py-2 text-xs'
          : // Desktop floating card: 2026-07-02 (Georgetown feedback) moved
            // from bottom-left to TOP-CENTER of the map. Reviewers said
            // the legend was easy to miss down at the bottom; pinning
            // to the top of the viewport surfaces it first. Sits above
            // Leaflet's zoom controls (which live in the top-right at
            // z-1000 within the map pane) via z-[500] on the parent
            // stacking context. `left-1/2 -translate-x-1/2` centers it
            // horizontally without needing the sidebar-offset math the
            // old bottom-left position used.
            'absolute top-4 left-1/2 -translate-x-1/2 z-[500] hidden items-center gap-4 rounded-lg border border-border bg-white px-4 py-2.5 text-xs shadow-panel md:flex'
      }
    >
      {/* Ratio color scale */}
      <div className={isInline ? 'min-w-0 flex-1' : 'min-w-[180px]'}>
        <p className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.1em] text-text-4">
          Credentials-to-jobs ratio
        </p>
        <div
          aria-hidden="true"
          className="h-[8px] w-full rounded-full"
          style={{ background: gradient }}
        />
        <div className="mt-0.5 flex justify-between text-[10px] font-semibold text-text-3">
          {RATIO_TICKS.map((tick) => (
            <span key={tick.label}>{tick.label}</span>
          ))}
        </div>
        <p className="mt-1 text-[10px] leading-[1.4] text-text-4">
          Below 1.0 = shortage · Above 1.0 = surplus
        </p>
      </div>

      {/* Vertical divider — desktop only; on inline mobile the
          flex gap is enough separation and a divider just steals
          horizontal real estate. */}
      {!isInline && (
        <div aria-hidden="true" className="h-14 w-px self-stretch bg-border-light" />
      )}

      {/* Bubble size scale */}
      <div className={isInline ? 'flex-none' : undefined}>
        <p className="mb-1.5 text-[10px] font-extrabold uppercase tracking-[0.1em] text-text-4">
          Bubble size
        </p>
        <div className="flex items-end gap-[8px]">
          {SIZE_SAMPLES.map((s) => {
            const px = sampleSizePx(s.ratio);
            return (
              <div key={s.label} className="flex flex-col items-center gap-[3px]">
                <span
                  aria-hidden="true"
                  className="rounded-full"
                  style={{
                    width: px,
                    height: px,
                    background: ratioColor(s.ratio),
                  }}
                />
                <span className="text-[10px] font-semibold text-text-3">{s.label}</span>
              </div>
            );
          })}
          {/* Caption: inline next to bubbles on desktop, hidden
              on the compact mobile inline variant where the labels
              (0.3 / 1.0 / 1.6) already imply the relationship. */}
          {!isInline && (
            <span className="mb-1 ml-1 whitespace-nowrap text-[10px] leading-[1.3] text-text-4">
              larger = bigger gap
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
